import { create } from "zustand";
import assetInspectionService, {
  AssetChecklist,
  InspectionAssessment,
} from "../service/assetInspectionService";

interface AssetInspectionState {
  // Asset Checklists
  assetChecklists: AssetChecklist[];
  assetChecklistsLoading: boolean;
  assetChecklistsError: string | null;
  currentAssetChecklist: AssetChecklist | null;

  // Assessments
  assessments: InspectionAssessment[];
  assessmentsLoading: boolean;
  assessmentsError: string | null;
  currentAssessment: InspectionAssessment | null;

  // Dashboard Stats
  dashboardStats: any;
  dashboardStatsLoading: boolean;

  // Pagination
  assetChecklistsPagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  assessmentsPagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };

  // Actions - Asset Checklists
  fetchAssetChecklists: (params?: any) => Promise<void>;
  fetchAssetChecklistById: (id: string) => Promise<void>;
  createAssetChecklist: (
    data: Partial<AssetChecklist>
  ) => Promise<AssetChecklist>;
  updateAssetChecklist: (
    id: string,
    data: Partial<AssetChecklist>
  ) => Promise<AssetChecklist>;
  deleteAssetChecklist: (id: string) => Promise<void>;
  suggestAssetChecklists: (
    equipmentInventoryId: string,
    equipmentSubCategoryId?: string,
    assetType?: string
  ) => Promise<AssetChecklist[]>;
  // Duplicate functionality not needed
  // duplicateAssetChecklist: (id: string, newName?: string, createdBy?: string) => Promise<AssetChecklist>;
  toggleAssetChecklistStatus: (id: string) => Promise<void>;

  // Actions - Assessments
  fetchAssessments: (params?: any) => Promise<void>;
  fetchAssessmentById: (id: string) => Promise<void>;
  createAssessment: (
    data: Partial<InspectionAssessment>
  ) => Promise<InspectionAssessment>;
  updateAssessment: (
    id: string,
    data: Partial<InspectionAssessment>
  ) => Promise<InspectionAssessment>;
  deleteAssessment: (id: string) => Promise<void>;
  fetchAssetAssessments: (assetId: string, params?: any) => Promise<void>;
  submitDraft: (id: string) => Promise<void>;
  reviewAssessment: (
    id: string,
    status: "reviewed" | "approved" | "rejected",
    reviewNotes?: string,
    reviewedBy?: string
  ) => Promise<void>;

  // Actions - Dashboard
  fetchDashboardStats: (startDate?: string, endDate?: string) => Promise<void>;

  // Reset Actions
  resetAssetChecklists: () => void;
  resetAssessments: () => void;
  resetAll: () => void;
}

export const useAssetInspectionStore = create<AssetInspectionState>(
  (set, get) => ({
    // Initial state
    assetChecklists: [],
    assetChecklistsLoading: false,
    assetChecklistsError: null,
    currentAssetChecklist: null,

    assessments: [],
    assessmentsLoading: false,
    assessmentsError: null,
    currentAssessment: null,

    dashboardStats: null,
    dashboardStatsLoading: false,

    assetChecklistsPagination: {
      page: 1,
      limit: 10,
      total: 0,
      pages: 0,
    },
    assessmentsPagination: {
      page: 1,
      limit: 10,
      total: 0,
      pages: 0,
    },

    // Asset Checklist Actions
    fetchAssetChecklists: async (params = {}) => {
      set({ assetChecklistsLoading: true, assetChecklistsError: null });
      try {
        const result = await assetInspectionService.getAssetChecklists(params);
        set({
          assetChecklists: result.checklists,
          assetChecklistsPagination: result.pagination,
          assetChecklistsLoading: false,
        });
      } catch (error: any) {
        set({
          assetChecklistsError:
            error.message || "Failed to fetch asset checklists",
          assetChecklistsLoading: false,
        });
        throw error;
      }
    },

    fetchAssetChecklistById: async (id: string) => {
      set({ assetChecklistsLoading: true, assetChecklistsError: null });
      try {
        const checklist = await assetInspectionService.getAssetChecklistById(
          id
        );
        set({
          currentAssetChecklist: checklist,
          assetChecklistsLoading: false,
        });
      } catch (error: any) {
        set({
          assetChecklistsError:
            error.message || "Failed to fetch asset checklist",
          assetChecklistsLoading: false,
        });
        throw error;
      }
    },

    createAssetChecklist: async (data: Partial<AssetChecklist>) => {
      try {
        const checklist = await assetInspectionService.createAssetChecklist(
          data
        );
        set((state) => ({
          assetChecklists: [checklist, ...state.assetChecklists],
        }));
        return checklist;
      } catch (error: any) {
        throw error;
      }
    },

    updateAssetChecklist: async (id: string, data: Partial<AssetChecklist>) => {
      try {
        const checklist = await assetInspectionService.updateAssetChecklist(
          id,
          data
        );
        set((state) => ({
          assetChecklists: state.assetChecklists.map((c) =>
            c._id === id || c.id === id ? checklist : c
          ),
          currentAssetChecklist:
            state.currentAssetChecklist?._id === id ||
            state.currentAssetChecklist?.id === id
              ? checklist
              : state.currentAssetChecklist,
        }));
        return checklist;
      } catch (error: any) {
        throw error;
      }
    },

    deleteAssetChecklist: async (id: string) => {
      try {
        await assetInspectionService.deleteAssetChecklist(id);
        set((state) => ({
          assetChecklists: state.assetChecklists.filter(
            (c) => c._id !== id && c.id !== id
          ),
        }));
      } catch (error: any) {
        throw error;
      }
    },

    suggestAssetChecklists: async (
      equipmentInventoryId: string,
      equipmentSubCategoryId?: string,
      assetType?: string
    ) => {
      try {
        return await assetInspectionService.suggestAssetChecklists(
          equipmentInventoryId,
          equipmentSubCategoryId,
          assetType
        );
      } catch (error: any) {
        throw error;
      }
    },

    // Duplicate functionality not needed
    // duplicateAssetChecklist: async (id: string, newName?: string, createdBy?: string) => {
    //   try {
    //     const checklist = await assetInspectionService.duplicateAssetChecklist(id, newName, createdBy);
    //     set(state => ({
    //       assetChecklists: [checklist, ...state.assetChecklists]
    //     }));
    //     return checklist;
    //   } catch (error: any) {
    //     throw error;
    //   }
    // },

    toggleAssetChecklistStatus: async (id: string) => {
      try {
        const result = await assetInspectionService.toggleAssetChecklistStatus(
          id
        );
        set((state) => ({
          assetChecklists: state.assetChecklists.map((c) =>
            c._id === id || c.id === id
              ? { ...c, isActive: result.isActive }
              : c
          ),
        }));
      } catch (error: any) {
        throw error;
      }
    },

    // Assessment Actions
    fetchAssessments: async (params = {}) => {
      set({ assessmentsLoading: true, assessmentsError: null });
      try {
        const result = await assetInspectionService.getAssessments(params);
        set({
          assessments: result.assessments,
          assessmentsPagination: result.pagination,
          assessmentsLoading: false,
        });
      } catch (error: any) {
        set({
          assessmentsError: error.message || "Failed to fetch assessments",
          assessmentsLoading: false,
        });
        throw error;
      }
    },

    fetchAssessmentById: async (id: string) => {
      set({ assessmentsLoading: true, assessmentsError: null });
      try {
        const assessment = await assetInspectionService.getAssessmentById(id);
        set({
          currentAssessment: assessment,
          assessmentsLoading: false,
        });
      } catch (error: any) {
        set({
          assessmentsError: error.message || "Failed to fetch assessment",
          assessmentsLoading: false,
        });
        throw error;
      }
    },

    createAssessment: async (data: Partial<InspectionAssessment>) => {
      try {
        const assessment = await assetInspectionService.createAssessment(data);
        set((state) => ({
          assessments: [assessment, ...state.assessments],
        }));
        return assessment;
      } catch (error: any) {
        throw error;
      }
    },

    updateAssessment: async (
      id: string,
      data: Partial<InspectionAssessment>
    ) => {
      try {
        const assessment = await assetInspectionService.updateAssessment(
          id,
          data
        );
        set((state) => ({
          assessments: state.assessments.map((a) =>
            a._id === id || a.id === id ? assessment : a
          ),
          currentAssessment:
            state.currentAssessment?._id === id ||
            state.currentAssessment?.id === id
              ? assessment
              : state.currentAssessment,
        }));
        return assessment;
      } catch (error: any) {
        throw error;
      }
    },

    deleteAssessment: async (id: string) => {
      try {
        await assetInspectionService.deleteAssessment(id);
        set((state) => ({
          assessments: state.assessments.filter(
            (a) => a._id !== id && a.id !== id
          ),
        }));
      } catch (error: any) {
        throw error;
      }
    },

    fetchAssetAssessments: async (assetId: string, params = {}) => {
      set({ assessmentsLoading: true, assessmentsError: null });
      try {
        const result = await assetInspectionService.getAssetAssessments(
          assetId,
          params
        );
        set({
          assessments: result.assessments,
          assessmentsPagination: result.pagination,
          assessmentsLoading: false,
        });
      } catch (error: any) {
        set({
          assessmentsError:
            error.message || "Failed to fetch asset assessments",
          assessmentsLoading: false,
        });
        throw error;
      }
    },

    submitDraft: async (id: string) => {
      try {
        const assessment = await assetInspectionService.submitDraft(id);
        set((state) => ({
          assessments: state.assessments.map((a) =>
            a._id === id || a.id === id ? assessment : a
          ),
          currentAssessment:
            state.currentAssessment?._id === id ||
            state.currentAssessment?.id === id
              ? assessment
              : state.currentAssessment,
        }));
      } catch (error: any) {
        throw error;
      }
    },

    reviewAssessment: async (
      id: string,
      status: "reviewed" | "approved" | "rejected",
      reviewNotes?: string,
      reviewedBy?: string
    ) => {
      try {
        const assessment = await assetInspectionService.reviewAssessment(
          id,
          status,
          reviewNotes,
          reviewedBy
        );
        set((state) => ({
          assessments: state.assessments.map((a) =>
            a._id === id || a.id === id ? assessment : a
          ),
          currentAssessment:
            state.currentAssessment?._id === id ||
            state.currentAssessment?.id === id
              ? assessment
              : state.currentAssessment,
        }));
      } catch (error: any) {
        throw error;
      }
    },

    // Dashboard Actions
    fetchDashboardStats: async (startDate?: string, endDate?: string) => {
      set({ dashboardStatsLoading: true });
      try {
        const stats = await assetInspectionService.getDashboardStats(
          startDate,
          endDate
        );
        set({
          dashboardStats: stats,
          dashboardStatsLoading: false,
        });
      } catch (error: any) {
        set({ dashboardStatsLoading: false });
        throw error;
      }
    },

    // Reset Actions
    resetAssetChecklists: () => {
      set({
        assetChecklists: [],
        assetChecklistsLoading: false,
        assetChecklistsError: null,
        currentAssetChecklist: null,
        assetChecklistsPagination: {
          page: 1,
          limit: 10,
          total: 0,
          pages: 0,
        },
      });
    },

    resetAssessments: () => {
      set({
        assessments: [],
        assessmentsLoading: false,
        assessmentsError: null,
        currentAssessment: null,
        assessmentsPagination: {
          page: 1,
          limit: 10,
          total: 0,
          pages: 0,
        },
      });
    },

    resetAll: () => {
      set({
        assetChecklists: [],
        assetChecklistsLoading: false,
        assetChecklistsError: null,
        currentAssetChecklist: null,
        assessments: [],
        assessmentsLoading: false,
        assessmentsError: null,
        currentAssessment: null,
        dashboardStats: null,
        dashboardStatsLoading: false,
        assetChecklistsPagination: {
          page: 1,
          limit: 10,
          total: 0,
          pages: 0,
        },
        assessmentsPagination: {
          page: 1,
          limit: 10,
          total: 0,
          pages: 0,
        },
      });
    },
  })
);
